import classNames from "classnames";
import * as _ from "lodash-es";
import { DateTime } from "luxon";
import type { MRT_Column, MRT_Row } from "mantine-react-table-open";
import { useState } from "react";
import { MantineRoundedNumberCell } from "../../../../../mantine/mrt/components/cell/MantineRoundedNumberCell";
import { getColumnDataKey } from "../../../../../mantine/mrt/utils/getColumnDataKey";
import { useToast } from "../../../../../mantine/notifications/useToast";
import type { EnergyRevenuesCellCheckStateColumn } from "../../../../../types/api.types";
import { EnergyRevenuesCellCheckStateState } from "../../../../../types/api.types";
import { backendDateOrDateTimeToLuxonDateTime } from "../../../../../utils/dates/backendDateOrDateTimeToLuxonDateTime";
import { BasicConfirmationModal } from "../../../../BuildingBlocks/Layout/Modals/BasicConfirmationModal/BasicConfirmationModal";
import { DataVerificationCell } from "../../../DataVerificationCell/DataVerificationCell";
import { useEnergyRevenuesVerificationMutations } from "../../../hooks/useEnergyRevenuesVerificationMutations";
import type {
  EnergyRevenuesCellChecksResponse,
  EnergyRevenuesCellCheckState,
  EnergyRevenuesCellCheckStateResponse,
  EnergyRevenuesListItem
} from "../../../RevenueMonitoring.types";
import "./CustomTableCell.scss";
import { NullCell } from "./NullCell";

function getVerificationItemForPeriodAndColumn(
  verifications: EnergyRevenuesCellChecksResponse = [],
  period_start: string,
  column: EnergyRevenuesCellCheckStateColumn
): EnergyRevenuesCellCheckStateResponse | undefined {
  return verifications.find(
    (item) => item.period_start === period_start && item.column === column
  );
}

interface CustomTableCellProps {
  row: MRT_Row<EnergyRevenuesListItem>;
  column: MRT_Column<EnergyRevenuesListItem>;
  companyId?: number;
  dataVerificationColumns?: Array<EnergyRevenuesCellCheckStateColumn>;
  dataVerificationItems?: EnergyRevenuesCellChecksResponse;
  warningMessage?: string;
}

function CustomTableCell({
  row,
  column,
  companyId,
  dataVerificationColumns,
  dataVerificationItems,
  warningMessage
}: CustomTableCellProps) {
  const { showToast, showErrorToast } = useToast();

  const columnKey = getColumnDataKey(
    column.columnDef
  ) as EnergyRevenuesCellCheckStateColumn;
  const valueInRow = row.original[columnKey];

  const rowDate = backendDateOrDateTimeToLuxonDateTime(
    row.original.period_start
  );
  const value = Number(row.original[columnKey]);
  const isCurrentMonth =
    DateTime.now().startOf("month") <= rowDate &&
    rowDate <= DateTime.now().endOf("month");

  const [itemToConfirmUpdate, setItemToConfirmUpdate] = useState<
    EnergyRevenuesCellCheckStateResponse | undefined
  >(undefined);

  const { createVerificationMutation, updateVerificationMutation } =
    useEnergyRevenuesVerificationMutations(companyId);

  const isPending =
    createVerificationMutation.isPending ||
    updateVerificationMutation.isPending;

  function handleVerificationClick(
    verificationItemToUpdate?: EnergyRevenuesCellCheckStateResponse
  ) {
    if (verificationItemToUpdate) {
      setItemToConfirmUpdate(verificationItemToUpdate);
      return;
    }

    if (companyId) {
      createVerificationMutation.mutate({
        person_id: companyId,
        period_start: row.original.period_start,
        column: columnKey,
        state: EnergyRevenuesCellCheckStateState.inProgress,
        calculated_amount: value
      });
    }
  }

  function handleVerificationConfirm() {
    if (itemToConfirmUpdate) {
      const itemWithoutReadOnlyProps = _.omit(itemToConfirmUpdate, [
        "id",
        "timestamp_of_state_change",
        "author_of_state_change_name"
      ]);

      const updatedItem: EnergyRevenuesCellCheckState = {
        ...itemWithoutReadOnlyProps,
        state: EnergyRevenuesCellCheckStateState.inProgress,
        calculated_amount: value
      };

      updateVerificationMutation.mutate(
        {
          verificationItem: updatedItem,
          verificationId: itemToConfirmUpdate.id
        },
        {
          onSuccess: () => {
            setItemToConfirmUpdate(undefined);
            showToast({
              type: "success",
              message: "Der zu prüfende Wert wurde erfolgreich ausgetauscht."
            });
          },
          onError: (error) => showErrorToast(error)
        }
      );
    }
  }

  if (valueInRow === undefined || valueInRow === null) {
    return <NullCell />;
  }

  return (
    <div
      className={classNames("CustomTableCell", {
        "has-warning": !!warningMessage
      })}
    >
      {dataVerificationColumns?.includes(columnKey) ? (
        <>
          <DataVerificationCell
            isCurrentMonth={isCurrentMonth}
            isPending={isPending}
            value={value}
            verificationItem={getVerificationItemForPeriodAndColumn(
              dataVerificationItems,
              row.original.period_start,
              columnKey
            )}
            warningMessage={warningMessage}
            onVerificationClick={handleVerificationClick}
          />
          <BasicConfirmationModal
            confirmationButtonColor="warning"
            confirmationButtonText="Änderung vornehmen"
            isLoading={isPending}
            opened={!!itemToConfirmUpdate}
            title="Prüfung erneuern?"
            toggleModal={() => setItemToConfirmUpdate(undefined)}
            withinPortal={false}
            onCancel={() => setItemToConfirmUpdate(undefined)}
            onConfirm={handleVerificationConfirm}
          >
            Der bestehende, zu prüfende Wert wird in der Prüfansicht durch den
            hier neu errechneten Wert ausgetauscht. Der ursprüngliche Wert kann
            in der Historie nachvollzogen werden.
          </BasicConfirmationModal>
        </>
      ) : (
        <MantineRoundedNumberCell
          extraSpaceRight
          value={value}
          warningMessage={warningMessage}
        />
      )}
    </div>
  );
}

export { CustomTableCell };
